import React, { useMemo } from 'react';
import { Drawer, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useReturnReceiptAcceptance } from '@contexts/operator/ReturnReceiptAcceptanceContext';
import { VerificationFormPanel } from '@components/operator/shared/inventory_verification';
import type {
  ProductFlags,
  VerificationFormValues,
  AttributeDefinition,
} from '@components/operator/shared/inventory_verification';

const { Text } = Typography;

// Replaces the legacy step-based <ReturnReceiptVerificationModal /> with a
// single-page Drawer that delegates field rendering to the shared
// <VerificationFormPanel />. The drawer is purely glue: it reads the current
// scannedItem off ReturnReceiptAcceptanceContext, maps it into the panel's
// props, and forwards the panel's onSubmit into verifyItem(itemId, payload).
const ReturnVerificationDrawer: React.FC = () => {
  const { t } = useTranslation();
  const {
    scannedItem,
    setScannedItem,
    inventoryTypes,
    attributeDefinitions,
    verifyItem,
    defaultReturnInventoryTypeId,
  } = useReturnReceiptAcceptance();

  const open = !!scannedItem;
  const isUnplanned = !!scannedItem?.unplanned;

  const product = scannedItem?.product ?? null;

  const productFlags = useMemo<ProductFlags>(() => {
    // 「是否需要序號」由 count > 0 推導；FE / hook 都對齊這個推導，沒有獨立 flag。
    const count =
      typeof product?.required_serial_numbers_count === 'number'
        ? product.required_serial_numbers_count
        : 0;
    return {
      validity_days: typeof product?.validity_days === 'number' ? product.validity_days : 0,
      batch_management_required: !!product?.batch_management_required,
      manufacturing_date_required: !!product?.manufacturing_date_required,
      required_serial_numbers_count: count,
      serial_number_pattern: (product?.serial_number_pattern as string | null | undefined) ?? null,
      box_serial_number_delimiter:
        (product?.box_serial_number_delimiter as string | null | undefined) ?? null,
      product_barcode: (product?.barcode as string | null | undefined) ?? null,
    };
  }, [product]);

  const initialValues = useMemo<Partial<VerificationFormValues>>(() => {
    if (!scannedItem) return {};
    const received = scannedItem.received_quantity ?? 0;
    const scheduled = scannedItem.quantity ?? 0;
    // When resuming an in-progress row (received_quantity > 0) preserve the
    // existing count so the operator can adjust from there. For fresh planned
    // rows use remaining; unplanned rows have scheduled = 0 so we land on 1.
    const targetQty = received > 0 ? received : Math.max(1, scheduled - received);
    return {
      quantity: targetQty,
      batch: scannedItem.batch ?? null,
      expiration_date: scannedItem.expiration_date ?? null,
      manufacturing_date: scannedItem.manufacturing_date ?? null,
      inventory_type_id:
        defaultReturnInventoryTypeId || scannedItem.inventory_type_id || undefined,
      custom_attributes:
        (scannedItem.custom_attributes as Record<string, unknown> | null | undefined) ?? {},
    };
  }, [scannedItem, defaultReturnInventoryTypeId]);

  const handleSubmit = async (values: VerificationFormValues, serials: string[]): Promise<void> => {
    if (!scannedItem) return;
    verifyItem(scannedItem.id, {
      product_id: scannedItem.product_id,
      received_quantity: values.quantity,
      inventory_type_id: values.inventory_type_id,
      batch: values.batch || null,
      expiration_date: values.expiration_date ?? null,
      manufacturing_date: values.manufacturing_date ?? null,
      custom_attributes: values.custom_attributes ?? null,
      serial_numbers: serials,
      // Preserve scheduled quantity so the context's mismatch detector can
      // continue to compare against the original plan.
      quantity: scannedItem.quantity ?? 0,
    });
  };

  const handleClose = (): void => {
    setScannedItem(null);
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={720}
      destroyOnClose
      title={
        scannedItem ? (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Space>
              <Text strong>{(scannedItem.product?.name as string | undefined) || ''}</Text>
              {isUnplanned && (
                <Tag color="orange">
                  {t('operator.return_verification.unplanned', { defaultValue: 'Unplanned' })}
                </Tag>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 13 }}>
              SKU: {(scannedItem.product?.sku as string | undefined) || ''}
              {(scannedItem.quantity ?? 0) > 0 && (
                <span style={{ marginLeft: 16 }}>
                  {t('operator.return_verification.expected_qty', { defaultValue: 'Expected' })}:{' '}
                  {scannedItem.quantity}
                </span>
              )}
            </Text>
          </Space>
        ) : null
      }
    >
      {scannedItem && (
        <VerificationFormPanel
          productFlags={productFlags}
          initialValues={initialValues}
          initialSerials={(scannedItem.serial_numbers as string[] | undefined) ?? []}
          quantityMode="single"
          attributeDefinitions={(attributeDefinitions || []) as unknown as AttributeDefinition[]}
          inventoryTypes={(inventoryTypes || []).map((it) => ({
            id: it.id,
            name: (it.name as string | undefined) ?? '',
          }))}
          onSubmit={handleSubmit}
          onCancel={handleClose}
        />
      )}
    </Drawer>
  );
};

export default ReturnVerificationDrawer;
